import React, { forwardRef, InputHTMLAttributes } from 'react'
import { clsx } from 'clsx'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  isLoading?: boolean
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({
    label,
    error,
    hint,
    leftIcon,
    rightIcon,
    isLoading,
    className,
    disabled,
    ...props
  }, ref) => {
    const inputId = props.id || `input-${crypto.randomUUID()}`

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {label}
            {props.required && <span className="text-danger-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <div className="text-gray-400 text-sm">
                {leftIcon}
              </div>
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            disabled={disabled || isLoading}
            className={clsx(
              'block w-full rounded-md border shadow-sm',
              'focus:outline-none focus:ring-2 focus:ring-offset-0',
              'placeholder-gray-400 text-gray-900',
              'transition-colors duration-200',
              'text-base sm:text-sm',
              leftIcon ? 'pl-10' : 'pl-3 sm:pl-4',
              rightIcon ? 'pr-10' : 'pr-3 sm:pr-4',
              'py-2 sm:py-2.5',
              'min-h-[44px]',
              error
                ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-500'
                : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
              (disabled || isLoading) && 'bg-gray-50 text-gray-500 cursor-not-allowed',
              className
            )}
            {...props}
          />

          {(rightIcon || isLoading) && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              ) : (
                <div className="text-gray-400 text-sm">
                  {rightIcon}
                </div>
              )}
            </div>
          )}
        </div>

        {(error || hint) && (
          <div className="mt-1">
            {error && (
              <p className="text-sm text-danger-600">{error}</p>
            )}
            {hint && !error && (
              <p className="text-sm text-gray-500">{hint}</p>
            )}
          </div>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export default Input
